'use client';

import { c as _c } from "react/compiler-runtime";
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { useIsMedium } from '@/hooks/useBreakpoints';
import { useTrackPortfolioLendPositionsViewed } from '@/lib/analytics/client/hooks/useTrackPortfolioLendPositionsViewed';
import DashboardLendPositionsDesktopView from './components/DashboardLendPositionsDesktopView';
import DashboardLendPositionsMobileView from './components/DashboardLendPositionsMobileView';
import { useDashboardLendPositionsTable } from './hooks/useDashboardLendPositionsTable';

/**
 * Unlike the earn/borrow sections this renders nothing while loading, on error, or when the
 * viewed account has no lend positions: lending starts on a market page, so an always-on section
 * would be noise for virtually every user, and an API outage already shows in the siblings. A
 * partial list keeps the rows it could read, under a notice that it may be incomplete, and one
 * with no readable row shows the service-unavailable state.
 */
const DashboardLendPositions = () => {
  const $ = _c(20);
  const isMobile = useIsMedium();
  const {
    lendPositionsRows,
    facets,
    partial,
    loading,
    error,
    isStaleAccountData,
    table,
    filters,
    handleFiltersChange,
    positionChainIds,
    columnLabels
  } = useDashboardLendPositionsTable();
  const isSettled = !loading && !error && !isStaleAccountData;
  const isVisible = isSettled && lendPositionsRows.length > 0;
  const isUnreadable = isSettled && partial && lendPositionsRows.length === 0;
  let t0;
  if ($[0] !== isVisible || $[1] !== lendPositionsRows.length) {
    t0 = {
      active: isVisible,
      positionCount: lendPositionsRows.length
    };
    $[0] = isVisible;
    $[1] = lendPositionsRows.length;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  useTrackPortfolioLendPositionsViewed(t0);
  if (isUnreadable) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-col gap-6"><span className="text-foreground text-sm">Lend positions</span><ErrorServiceUnavailable /></div>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (!isVisible) {
    return null;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-foreground text-sm">Lend positions</span>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== partial) {
    t2 = partial ? <p data-testid="dashboard-lend-positions-partial-notice" className="text-secondary-foreground text-sm">Some positions could not be loaded, so this list may be incomplete.</p> : null;
    $[5] = partial;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== columnLabels || $[8] !== facets || $[9] !== filters || $[10] !== handleFiltersChange || $[11] !== isMobile || $[12] !== lendPositionsRows.length || $[13] !== loading || $[14] !== positionChainIds || $[15] !== table) {
    t3 = isMobile ? <DashboardLendPositionsMobileView table={table} filters={filters} onFiltersChange={handleFiltersChange} positionChainIds={positionChainIds} loading={loading} disabled={false} totalPositionsCount={lendPositionsRows.length} facets={facets} /> : <DashboardLendPositionsDesktopView table={table} filters={filters} onFiltersChange={handleFiltersChange} positionChainIds={positionChainIds} columnLabels={columnLabels} totalPositionsCount={lendPositionsRows.length} loading={loading} disabled={false} facets={facets} />;
    $[7] = columnLabels;
    $[8] = facets;
    $[9] = filters;
    $[10] = handleFiltersChange;
    $[11] = isMobile;
    $[12] = lendPositionsRows.length;
    $[13] = loading;
    $[14] = positionChainIds;
    $[15] = table;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t2 || $[18] !== t3) {
    t4 = <div className="flex flex-col gap-6">{t1}{t2}{t3}</div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};
export default DashboardLendPositions;